import { Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { RecruiterContact } from 'features/co-pilot/co-pilot.types';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import PaiPagination from 'features/ui/base-components/pai-pagination';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import RecruiterContactItem from './recruiter-contact-item';

interface Props {
  contacts: RecruiterContact[];
  contactsLoading: boolean;
  totalContacts: number;
  pageNumber: number;

  handleChangeRecruiterSearchPage(event: unknown, newPage: number): void;
}

const RecruiterContactsTable: React.FC<Props> = ({ contacts, contactsLoading, totalContacts, pageNumber, handleChangeRecruiterSearchPage }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Stack sx={{ height: '100%' }}>
      <div className={classes.tableHeader}>
        <Typography className={classes.headerText} mb={'4px'}>
          {t('searchResults')}
        </Typography>
        <Typography className={classes.greyText}>{t('searchResultsDescription')}</Typography>
      </div>
      <TableContainer className={classes.table}>
        <Table size={'small'} stickyHeader={true}>
          <TableHead className={classes.tableHeadRow}>
            <TableRow>
              <TableCell style={{ width: '33%' }}>{t('name')}</TableCell>
              <TableCell style={{ width: '33%' }}>{t('position')}</TableCell>
              <TableCell style={{ width: '33%' }}>{t('company')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contactsLoading ? (
              Array.from({ length: 25 }, (_item, index) => index).map((_item, index) => (
                <TableRow key={index}>
                  {Array.from({ length: 4 }, (_item, index) => index).map((_item, index2) => (
                    <TableCell key={index2} style={{ textAlign: 'center', borderBottom: 'none' }}>
                      <Skeleton variant={'rectangular'} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : !_.isEmpty(contacts) ? (
              contacts.map((contact, index) => <RecruiterContactItem key={index} contact={contact} />)
            ) : (
              <TableRow>
                <TableCell colSpan={4} style={{ textAlign: 'center' }}>
                  {t('noContactsFound')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack className={classes.tableFooter} direction={'row'} alignItems={'flex-end'} justifyContent={'space-between'}>
        <Stack direction={'row'} alignItems={'center'} gap={2} sx={{ height: '100%' }}>
          {contactsLoading ? (
            <>
              <Typography className={classes.loadingText}>{t('loadingRecruiterContacts')}</Typography>
              <PaiDotLoader size={'small'} inline={true} />
            </>
          ) : (
            <Typography className={classes.highlightText}>{t('amountContacts', { count: totalContacts })}</Typography>
          )}
        </Stack>
        <PaiPagination
          itemsPerPage={25}
          page={pageNumber}
          total={totalContacts}
          handleChangePage={handleChangeRecruiterSearchPage}
          hidden={totalContacts === 0}
          disabled={contactsLoading}
        />
      </Stack>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  tableHeader: {
    padding: '16px 24px',
  },
  headerText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
  tableHeadRow: {
    position: 'sticky',
    top: 0,
    backgroundColor: '#FFF',
    zIndex: 9,
  },
  table: {
    '& th': {
      border: 'none',
      padding: '4px 24px',
      fontSize: theme.typography.pxToRem(13),
    },
    '& td, th': {
      border: 'none',
      borderBottom: '1px solid #ddd',
      padding: '8px 24px',
      textAlign: 'left',
      verticalAlign: 'middle',
      fontSize: theme.typography.pxToRem(13),
    },
  },
  tableFooter: {
    borderTop: `1px solid ${theme.palette.greys.lighter}`,
    padding: '8px 24px',
    marginTop: 'auto',
  },
  loadingText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  highlightText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
}));

export default RecruiterContactsTable;
